// @flow
/* eslint-disable react/prop-types,no-plusplus,react/no-access-state-in-setstate */
import React, { Component, useEffect } from 'react';
import queryString from 'query-string';
import ScrollLock from 'react-scrolllock';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import dayjsDuration from 'dayjs/plugin/duration';
import roundDate from '@justpark/helpers/src/roundDate/roundDate';
import { connect } from 'react-redux';
import { compose } from 'redux';
import omit from '@justpark/helpers/src/omit/omit';
import isEmpty from '@justpark/helpers/src/isEmpty/isEmpty';
import numbersHaveChanged from '@justpark/helpers/src/numbersHaveChanged/numbersHaveChanged';
import { withTranslation } from 'react-i18next';
import type { Node, Element } from 'react';
import Tabs, { TabItem } from '@justpark/ui/src/components/Tabs';
import type { WeekOptions } from '@justpark/ui/src/components/LongTermDurationPicker';
import bem from '@justpark/helpers/src/bem/bem';
import { useLazyQuery } from '@apollo/client';
import localDateTimeIso8601 from '@justpark/helpers/src/localDateTimeIso8601/localDateTimeIso8601';
import calculateDateDiff from '@justpark/helpers/src/calculateDateDiff/calculateDateDiff';
import DatePicker from '../DatePicker/DatePicker';
import { todayTomorrowDateFormat } from '../../../helpers/date';
import { getQueryParam, restrictPastBooking } from '../../../helpers/utils';
import BookingDate from './BookingDate';
import SearchInput from '../Input/SearchInput';
import { host } from '../../../helpers/url';
import type { State } from '../../../stores/reducer';
import withAnalytics from '../../Decorators/withAnalytics';
import { defaultSearchDates } from '../../../helpers/date';
import { gtagTrack, track } from '../../../helpers/Tracking/Analytics';
import withQueryParams from '../../../decorators/withQueryParams';
import getDayjsInstance from '../../../helpers/dates/getDayjsInstance';
import convertToDayjsStartOf from '../../../helpers/dates/convertToDayjsStartOf';
import convertToDayjs from '../../../helpers/dates/convertToDayjs';
import formatDate from '../../../helpers/dates/formatDate';
import GET_SEARCH from '../../../queries/search/getSearch.graphql';
import '../../../../static/scss/components/searchForm.scss';
import getDefaultBookingDurationHours from '../../../helpers/getDefaultBookingDurationHours';

dayjs.extend(isSameOrBefore);
dayjs.extend(dayjsDuration);

export type Dates = {
  arriving: string,
  leaving: ?string,
  monthlyDuration: ?string,
  monthlyType: ?string
};

type Props = {
  autoLookup?: boolean,
  modifier: string,
  placeholder?: string,
  browser: any,
  dates?: Dates,
  value?: string,
  children?: Node | Element<any>,
  onDateChange?: () => void,
  coords?: any,
  hideInput?: boolean,
  disableDefaultDates?: boolean,
  setRef?: () => void,
  cta?: string,
  invalidDatesCallback: () => void,
  name?: string,
  analytics: any,
  tabTheme?: string,
  partner?: string,
  t: (string) => string,
  showIcon?: boolean,
  geolocationEvent?: string,
  navigate: () => void,
  showLessThanOneMonth?: boolean,
  mobileReSearch?: boolean
};

type SearchState = {
  coords: any,
  googlePlaceId: string,
  isValidInput: boolean,
  datePickerVisible: boolean,
  dates: Dates,
  validDates: boolean,
  disableDefaultDates: boolean,
  sourceParam: string,
  awinParam: string
};

const restrictArrivingToPresent = (dates: Dates) => {
  const updatedDates = [...dates];
  const now = getDayjsInstance();

  if (updatedDates[0].arriving.startOf('hour').isSameOrBefore(now)) {
    updatedDates[0].arriving = now
      .hour(updatedDates[0].arriving.hour())
      .minute(updatedDates[0].arriving.minute());
  }

  return updatedDates;
};

const shouldMobileAutosearch = (browser: any, mobileReSearch: boolean) =>
  mobileReSearch;

const noop = () => {};

const SearchFormHooks = ({
  coords,
  dates,
  googlePlaceId,
  form,
  mobileReSearch,
  filterQueryString
}) => {
  const { arriving, leaving, monthlyDuration, monthlyType } = dates;

  const [getQuery] = useLazyQuery(GET_SEARCH);

  const startDate = arriving ? arriving.format(localDateTimeIso8601) : null;
  const endDate = leaving ? leaving.format(localDateTimeIso8601) : null;

  const value = form?.elements.q.value || '';

  useEffect(() => {
    if (mobileReSearch) {
      // Do not prefetch if we're going to auto search
      return;
    }

    if (
      coords &&
      'lat' in coords &&
      'lng' in coords &&
      value &&
      startDate &&
      (endDate || !!monthlyDuration)
    ) {
      const dateDiff = calculateDateDiff(startDate, endDate);

      if (dateDiff >= 90) {
        return;
      }

      const { lat, lng } = coords;

      getQuery({
        variables: {
          lat,
          lng,
          startDate,
          endDate,
          monthlyApplicableDays: monthlyDuration ? monthlyType : undefined,
          googlePlaceId,
          userQuery: value,
          filters: filterQueryString
        }
      });
    }
  }, [
    value,
    mobileReSearch,
    getQuery,
    coords,
    monthlyDuration,
    monthlyType,
    googlePlaceId,
    filterQueryString,
    startDate,
    endDate
  ]);

  return null;
};

const getFilterQueryString = (location, dates) => {
  if (!location) {
    return 'reservable';
  }

  const { arriving } = dates[0];
  const now = getDayjsInstance();
  const leadTime = dayjs.duration(arriving.diff(now)).asMinutes();
  const defaultPrebook = leadTime > 30;
  const filterQueryString = getQueryParam(location.search, 'filters');

  if (defaultPrebook && !filterQueryString) {
    return 'reservable';
  }

  return filterQueryString;
};

export class SearchForm extends Component<Props, SearchState> {
  constructor(props: Props) {
    super(props);

    this.onSubmit = this.onSubmit.bind(this);
    this.onClickHandler = this.onClickHandler.bind(this);
    this.onNativeDatePickerChange = this.onNativeDatePickerChange.bind(this);
    this.onInputChangeHandler = this.onInputChangeHandler.bind(this);
    this.onDatePickerDateChange = this.onDatePickerDateChange.bind(this);
    this.checkTime = this.checkTime.bind(this);
    this.setMonthlyType = this.setMonthlyType.bind(this);
    this.setIsMonthly = this.setIsMonthly.bind(this);
    this.setMonthlyDuration = this.setMonthlyDuration.bind(this);
    this.onDatePickerClose = this.onDatePickerClose.bind(this);

    const hasLocation = !!props.location;

    const sourceParam = hasLocation
      ? getQueryParam(props.location.search, 'source')
      : null;
    const awinParam = hasLocation
      ? getQueryParam(props.location.search, 'awc')
      : null;

    const { dates, coords, googlePlaceId, disableDefaultDates } = props;

    this.state = {
      coords,
      googlePlaceId,
      isValidInput: true,
      datePickerVisible: false,
      dates: [
        {
          arriving: convertToDayjsStartOf(dates.arriving),
          leaving: dates.leaving
            ? convertToDayjsStartOf(dates.leaving)
            : undefined,
          monthlyDuration: dates.monthlyDuration,
          monthlyType: dates.monthlyType
        }
      ],
      validDates: true,
      disableDefaultDates,
      sourceParam,
      awinParam
    };
  }

  componentDidMount() {
    this.props.setRef(this);
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { arriving, leaving, monthlyDuration, monthlyType } = nextProps.dates;
    const { coords, googlePlaceId } = nextProps;

    if (
      googlePlaceId !== this.props.googlePlaceId ||
      numbersHaveChanged(coords.lat, this.props.coords.lat) ||
      numbersHaveChanged(coords.lng, this.props.coords.lng)
    ) {
      // Sync props for coors and place ID into that state
      this.setState({
        coords,
        googlePlaceId
      });
    }

    if (
      arriving === this.props.dates.arriving &&
      leaving === this.props.dates.leaving &&
      monthlyDuration === this.props.dates.monthlyDuration &&
      monthlyType === this.props.dates.monthlyType
    ) {
      return;
    }

    this.setState({
      dates: [
        {
          arriving: convertToDayjsStartOf(arriving),
          leaving: leaving ? convertToDayjsStartOf(leaving) : undefined,
          monthlyDuration,
          monthlyType
        }
      ]
    });
  }

  onSubmit(e, data = {}) {
    const { name, partner, navigate, location } = this.props;

    if (this.props.noSubmit && e) {
      e.preventDefault();
    }

    if (isEmpty(this.state.coords)) {
      return this.shakeInputForm();
    }

    const formQuery = {};
    for (let i = 0; i < this.form.elements.length; i++) {
      const el = this.form.elements[i];

      if (!isEmpty(el.name)) {
        formQuery[el.name] = el.value;
      }
    }

    if (isEmpty(formQuery.q)) {
      formQuery.q = name;
    }

    const query = {
      ...formQuery,
      ...data
    };

    const {
      arriving,
      leaving,
      monthlyDuration,
      monthlyType
    } = this.state.dates[0];

    const isMonthlyBooking = !!monthlyDuration;

    if (isMonthlyBooking) {
      query.monthly_type = monthlyType;
    }

    const updatedQuery = omit(query, [
      'start_date',
      'start_time',
      'end_date',
      'end_time',
      'monthly_duration'
    ]);

    updatedQuery.arriving = formatDate(arriving);

    if (leaving) {
      updatedQuery.leaving = formatDate(leaving);
    }

    if (monthlyDuration) {
      updatedQuery.monthly_duration = monthlyDuration;
    }

    updatedQuery.coords = `${this.state.coords.lat},${this.state.coords.lng}`;

    const qs = queryString.stringify(updatedQuery);
    const partnerUrl = partner ? `/partner/${partner}` : '';

    gtagTrack('core:search-block:clicked-search', { isMonthlyBooking });

    const { pathname, search } = location;
    const currentUrl = `${pathname}${search}`;

    const newUrl = `${partnerUrl}/search/?${qs}`;
    const replace = currentUrl === newUrl;

    navigate(newUrl, {
      state: 'submit',
      replace
    });

    return false;
  }

  shakeInputForm() {
    track('core:search-block:viewed-search-error', { no_geo: true });
    this.setState({
      isValidInput: false
    });
    setTimeout(() => {
      this.setState({
        isValidInput: true
      });
    }, 500);

    return false;
  }

  highlightDates(enabled = true) {
    this.setState(
      {
        validDates: !enabled
      },
      () => {
        if (enabled && typeof this.props.invalidDatesCallback === 'function') {
          this.props.invalidDatesCallback();
        }
      }
    );
  }

  onClickHandler(target, shouldOpenNonNativePicker = true) {
    // Once the date component is interacted with allow the user to keep the default they have seen
    this.setState({
      disableDefaultDates: false
    });

    const { datePickerVisible } = this.state;

    if (datePickerVisible && typeof target === 'string') {
      if (target !== this.state.datePickerTarget) {
        this.setState({
          datePickerTarget: target
        });
        return;
      }

      this.setState({
        datePickerVisible: !datePickerVisible
      });
      return;
    }

    if (shouldOpenNonNativePicker) {
      this.setState({
        datePickerVisible: !datePickerVisible,
        datePickerTarget: target,
        disableDefaultDates: false,
        validDates: true
      });
      return;
    }

    this.setState({
      datePickerTarget: target
    });
  }

  /**
   * onDatePickerDateChange: Called by the fancy date picker component
   * @param which
   * @param value
   */
  onDatePickerDateChange(which, value) {
    const { dates } = this.state;
    const { arriving, leaving, monthlyDuration, monthlyType } = dates[0];

    const updatedDates = [
      {
        arriving,
        leaving,
        monthlyDuration,
        monthlyType
      }
    ];

    updatedDates[0][which] = convertToDayjs(value);

    this.checkTime(updatedDates);
  }

  onDatePickerClose() {
    // For the fancy date picker only call the parent component once the date picker has closed
    const { dates } = this.state;

    this.setState(
      {
        datePickerVisible: false
      },
      () => {
        this.props.onDateChange(dates);
      }
    );
  }

  /**
   * onNativeDatePickerChange - Called by the native date inputs
   * @param which
   * @param timestamp
   */
  onNativeDatePickerChange(which, timestamp) {
    const dates = [...this.state.dates];
    dates[0][which] = convertToDayjs(timestamp);

    const updatedDates = restrictArrivingToPresent(dates);

    this.checkTime(updatedDates);
  }

  checkTime(newDates = []) {
    const { dates } = this.state;
    const { browser } = this.props;
    const { arriving, monthlyDuration, monthlyType } = newDates[0] || dates[0];
    let { leaving } = newDates[0] || dates[0];

    if (leaving && leaving.isSameOrBefore(arriving)) {
      leaving = arriving.add(getDefaultBookingDurationHours(), 'hours');

      const updatedDates = [
        {
          arriving: arriving.startOf('minute'),
          leaving: leaving.startOf('minute'),
          monthlyDuration,
          monthlyType
        }
      ];

      this.handleDateChange(updatedDates);
      return;
    }

    const bookingDates = [...dates];

    bookingDates[0].monthlyType = monthlyType;
    bookingDates[0].arriving = restrictPastBooking(arriving);
    bookingDates[0].leaving = leaving;

    if (browser.greaterThan.extraSmall) {
      bookingDates[0].arriving = roundDate(bookingDates[0].arriving, 30);
      bookingDates[0].leaving = bookingDates[0].leaving
        ? roundDate(bookingDates[0].leaving, 30)
        : null;
    }

    this.handleDateChange(bookingDates);
  }

  setIsMonthly(isMonthly: boolean) {
    const { arriving } = defaultSearchDates();
    const { dates } = this.state;

    const monthlyArriving = arriving.add(3, 'days').startOf('day');

    this.props.onDateChange([
      {
        arriving: isMonthly ? monthlyArriving : arriving,
        leaving: isMonthly
          ? null
          : arriving.add(getDefaultBookingDurationHours(), 'hours'),
        monthlyDuration: isMonthly ? '0' : null,
        monthlyType: dates[0].monthlyType
      }
    ]);
  }

  /**
   * handleDateChange - Called by the check time function and calls the parent component with updated values
   * @param dates
   */
  handleDateChange(dates) {
    const { arriving, leaving, monthlyDuration, monthlyType } = dates[0];

    const updatedDates = [
      {
        arriving: arriving.startOf('minute'),
        leaving: leaving ? leaving.startOf('minute') : undefined,
        monthlyDuration: monthlyDuration || undefined,
        monthlyType: monthlyType || undefined
      }
    ];

    const { datePickerVisible } = this.state;

    this.setState(
      {
        disableDefaultDates: false,
        dates: updatedDates
      },
      () => {
        this.highlightDates(false);

        if (!datePickerVisible) {
          // Don't update dates if date picket is open.
          // Will instead be called in the onClose event
          this.props.onDateChange(updatedDates);
        }
      }
    );
  }

  setMonthlyType(option: WeekOptions) {
    const { browser, mobileReSearch, setMonthlyType } = this.props;

    if (option === '-1') {
      // Go to non-monthly flow
      return this.setMonthlyDuration(option);
    }

    const shouldAutoSearch = shouldMobileAutosearch(browser, mobileReSearch);

    return setMonthlyType(option, () => {
      if (shouldAutoSearch) {
        this.onSubmit();
      }
    });
  }

  setMonthlyDuration(duration) {
    const { dates } = this.state;

    const updatedDates = [{ ...dates[0] }];

    updatedDates[0].leaving = null;
    updatedDates[0].monthlyDuration = duration;

    this.handleDateChange(updatedDates);
  }

  /**
   * onInputChangeHandler - Called by the search input component
   * @param value
   * @param meta
   */
  onInputChangeHandler(value, meta) {
    const { browser, mobileReSearch } = this.props;
    const shouldAutoSearch = shouldMobileAutosearch(browser, mobileReSearch);

    // Handles address resolution from geo
    if ('coords' in meta) {
      this.setState(
        {
          coords: meta.coords,
          googlePlaceId: meta.googlePlaceId
        },
        () => {
          if (shouldAutoSearch) {
            this.onSubmit();
          }
        }
      );
    } else {
      this.setState({
        coords: null
      });
    }
  }

  generateClassnames() {
    const baseClassName = 'c-searchform';
    const classNames = [baseClassName];

    if (this.props.modifier) {
      classNames.push(`${baseClassName}--${this.props.modifier}`);
    }

    return classNames.join(' ');
  }

  render() {
    const className = this.generateClassnames();

    const {
      autoLookup,
      value,
      placeholder,
      browser,
      hideInput,
      modifier,
      allowScroll,
      cta,
      analytics,
      tabTheme,
      children,
      location,
      t,
      showIcon,
      geolocationEvent,
      showLessThanOneMonth,
      mobileReSearch
    } = this.props;

    const {
      isValidInput,
      disableDefaultDates,
      validDates,
      datePickerTarget,
      datePickerVisible,
      sourceParam,
      awinParam,
      dates,
      coords,
      googlePlaceId
    } = this.state;

    const filterQueryString = getFilterQueryString(location, dates);

    const { monthlyDuration } = dates[0];
    const isMonthly = !!monthlyDuration;
    const isMonthlyTab = isMonthly ? 1 : 0;

    const defaultPlaceholder = isMonthlyTab
      ? t('homePage:searchFormMonthlyPlaceholder', 'Enter location')
      : t(
          'homePage:searchFormDriveUpPlaceholder',
          'Enter location or location ID'
        );

    const inputPlaceholder = !placeholder ? defaultPlaceholder : placeholder;

    const searchForm = (
      <form
        ref={(el) => {
          this.form = el;
        }}
        action={`${host()}/search/`}
        className={className}
        onSubmit={this.onSubmit}
      >
        <SearchFormHooks
          coords={coords}
          dates={dates[0]}
          value={value}
          mobileReSearch={mobileReSearch}
          googlePlaceId={googlePlaceId}
          form={this.form}
          filterQueryString={filterQueryString}
        />
        <Tabs
          active={isMonthlyTab}
          theme={tabTheme}
          className={bem('c-searchform', 'tabs')}
          onChange={(index) => {
            const dateRange = index === 0 ? 'daily' : 'monthly';
            analytics.track(`click:search-block:toggle-${dateRange}`);
            this.setIsMonthly(index === 1);
          }}
          evenSpacing
        >
          <TabItem size="secondary" upperCase>
            {t('search:tabsHourlyDaily', 'Hourly / Daily')}
          </TabItem>
          <TabItem size="secondary" upperCase>
            {t('search:tabsMonthly', 'Monthly')}
          </TabItem>
        </Tabs>
        <SearchInput
          autoLookup={autoLookup}
          onChangeHandler={this.onInputChangeHandler}
          value={value}
          isValid={isValidInput}
          placeholder={inputPlaceholder}
          browser={browser}
          isHidden={hideInput}
          showGeoLocation
          label={t('search:searchInputLabel', 'Parking at')}
          showIcon={showIcon}
          geolocationEvent={geolocationEvent}
          showDriveup={!isMonthlyTab}
        />

        {googlePlaceId && (
          <input type="hidden" name="place_id" value={googlePlaceId} />
        )}
        {coords && (
          <input
            type="hidden"
            name="coords"
            value={`${coords.lat},${coords.lng}`}
          />
        )}

        {dates &&
          dates.map((res, idx) => {
            // this possibly can be done more effectively...
            if (modifier === 'single' && idx > 0) {
              return false;
            }

            const inputNameModifier = modifier === 'multi' ? '[]' : '';
            const dateFrom = disableDefaultDates
              ? false
              : todayTomorrowDateFormat(res.leaving, isMonthly, isMonthly, t);

            const dateTo = disableDefaultDates
              ? false
              : todayTomorrowDateFormat(res.arriving, isMonthly, isMonthly, t);

            return (
              <div
                key={idx}
                className={`c-booking-date__container ${
                  !validDates ? 'c-booking-date__container--invalid' : ''
                }`}
              >
                <BookingDate
                  dateFrom={dateFrom}
                  dateFromRaw={res.arriving}
                  datePickerTarget={datePickerTarget}
                  datePickerVisible={datePickerVisible}
                  dateTo={dateTo}
                  dateToRaw={res.leaving}
                  isValid={validDates}
                  modifier={modifier}
                  onClickHandler={this.onClickHandler}
                  onNativeDateChange={this.onNativeDatePickerChange}
                  isMonthly={isMonthly}
                  onMonthlyTypeChange={this.setMonthlyType}
                  monthlyType={res.monthlyType}
                  monthlyDuration={monthlyDuration}
                  onMonthlyChange={this.setMonthlyDuration}
                  showLessThanOneMonth={showLessThanOneMonth}
                />
                <input
                  type="hidden"
                  name={`start_date${inputNameModifier}`}
                  value={res.arriving.format('YYYY-MM-DD')}
                />
                {!!res.leaving && (
                  <input
                    type="hidden"
                    name={`end_date${inputNameModifier}`}
                    value={res.leaving.format('YYYY-MM-DD')}
                  />
                )}
                {!!res.monthlyDuration && (
                  <input
                    type="hidden"
                    name={`monthly_duration${inputNameModifier}`}
                    value={res.monthlyDuration}
                  />
                )}
                <input
                  type="hidden"
                  name={`start_time${inputNameModifier}`}
                  value={res.arriving.format('HH:mm')}
                />
                {!!res.leaving && (
                  <input
                    type="hidden"
                    name={`end_time${inputNameModifier}`}
                    value={res.leaving.format('HH:mm')}
                  />
                )}
                {sourceParam && (
                  <input type="hidden" name="source" value={sourceParam} />
                )}
                {awinParam && (
                  <input type="hidden" name="awc" value={awinParam} />
                )}
                {modifier === 'multi' && (
                  <div className="c-searchform__bt-remove" />
                )}
                {filterQueryString && (
                  <input
                    type="hidden"
                    name="filters"
                    value={filterQueryString}
                  />
                )}
              </div>
            );
          })}
        {modifier === 'multi' && (
          <div className="c-searchform__add">Add another reservation</div>
        )}

        {children}

        {datePickerVisible && (
          <div className="c-datepicker-container">
            {!allowScroll && <ScrollLock />}
            <DatePicker
              onClose={this.onDatePickerClose}
              activeTab={datePickerTarget}
              onDateChange={this.onDatePickerDateChange}
              leaving={dates[0].leaving}
              arriving={dates[0].arriving}
              checkTime={noop} // TODO: checkTime should probablly be removed from this component. Does it make sense to have a onDateChange and checkTime passed to it
              showTimePicker={!isMonthly}
              isOpen={datePickerVisible}
            />
          </div>
        )}
        {mobileReSearch === false && (
          <button
            type="submit"
            className="c-searchform__submit"
            data-cy="searchform-submit"
          >
            {cta}
          </button>
        )}
      </form>
    );

    return searchForm;
  }
}

const defaultSearchDatesValues = defaultSearchDates();

SearchForm.defaultProps = {
  children: null,
  value: '',
  autoLookup: true,
  coords: {},
  hideInput: false,
  disableDefaultDates: false,
  dates: {
    arriving: defaultSearchDatesValues.arriving.toDate(),
    leaving: defaultSearchDatesValues.leaving.toDate(),
    monthlyDuration: null
  },
  cta: 'Search',
  name: '',
  tabTheme: '',
  partner: '',
  setRef: () => {},
  onDateChange: () => {},
  showIcon: false,
  showLessThanOneMonth: false,
  mobileReSearch: false
};

const mapStateToProps = (state: State) => ({
  browser: state.browser,
  partner: state.brand.partner
});

export default compose(
  withAnalytics({}, {}),
  connect(mapStateToProps),
  withTranslation(),
  withQueryParams
)(SearchForm);
